import { RadioButtonWithLabelOption, TabStyles } from '@tableau/widgets';
import * as React from 'react';
import { style } from 'typestyle';
import { Messages } from '../../../../codegen/Localize';
import { FormikRadioButtonGroup } from '../../../../commonInfra/components/FormikRadioButtonGroup';
import { HelpService } from '../../../../commonInfra/services/HelpService';
import { FeatureFlags } from '../../../../ts/FeatureFlags';
import { componentRegistryEntry } from '../../../directives/tbSiteSettingsForServerAdminFormAdapter';
import { useFormikContext } from '../../../FormikWrapperForStubbing';
import { settingsRadioButtonItemHeight } from '../../../styles/CommonStyles';
import { UserVisibilityMode } from '../../../types/ISiteSettingsForSiteAdmin';
import { SiteSettingsForSiteAdminProps } from '../ReactSiteSettingsForSiteAdmin';
import { SettingsSection } from '../SettingsSection';

export type UserVisibilitySectionProps = SiteSettingsForSiteAdminProps<'userVisibility'>;
export type UserVisibilitySectionInternalProps = { helpService: HelpService } & UserVisibilitySectionProps;

function getButtons(): RadioButtonWithLabelOption<string>[] {
  return [{
    testId: 'userVisibility-full-radio-button',
    label: Messages.userVisibilityFullLabel(),
    value: 'FULL'
  }, {
    testId: 'userVisibility-limited-radio-button',
    label: Messages.userVisibilityLimitedLabel(),
    value: 'LIMITED'
  }];
}

function getDescription(userVisibilityHelpUrl: string) {
  return <>
    <span>{Messages.userVisibilityMsg()}</span>
    <span>&nbsp;</span>
    <a href={userVisibilityHelpUrl}>
      <span>{Messages.learnMore()}</span>
    </a>
  </>;
}

const warningStyle = style({
  marginTop: TabStyles.Spacing.MediumSpace,
  paddingLeft: TabStyles.Spacing.SmallSpace,
  fontStyle: TabStyles.Colors.AttentionLight,
  color: '#c41723'
});

export function DisplayWarning(userVisibilitySetting: UserVisibilityMode) {
  let warning: string;
  if (userVisibilitySetting === UserVisibilityMode.FULL) {
    warning = Messages.userVisibilityFullError();
  } else {
    if (FeatureFlags.collectionsEnabled) {
      // With feature flag removal, rename variable to userVisibilityLimitedError
      warning = Messages.collectionsUserVisibilityLimitedError();
    } else {
      warning = Messages.userVisibilityLimitedError();
    }
  }
  return <div className={warningStyle} id='user-visibility-warning-id'>
      { warning }
    </div>;
}

export function UserVisibilitySectionInternal(props: UserVisibilitySectionInternalProps) {
  if (!FeatureFlags.userVisibilityEnabled){
    return null;
  }

  const userVisibilityHelpUrl = props.helpService.getServerHelpUrlByCategory('user-visibility');
  const buttonList = React.useMemo(() => getButtons(), []);
  const formik = useFormikContext<UserVisibilitySectionProps>();

  return <SettingsSection title={Messages.userVisibility()} description={getDescription(userVisibilityHelpUrl)}>
    <FormikRadioButtonGroup
      name={'settings.userVisibility'}
      buttonList={buttonList}
      testId={'user-visibility-options'}
      className={settingsRadioButtonItemHeight}
      ariaLabel={Messages.userVisibility()}
    />
    { formik.touched?.settings?.userVisibility && DisplayWarning(formik.values.settings.userVisibility) }
  </SettingsSection>;
}

/**
 * A wrapper component which provides the dependencies to the internal component
 * @param props
 * @constructor
 */
export function UserVisibilitySection(props: UserVisibilitySectionProps) {
  const deps: UserVisibilitySectionInternalProps = {
    helpService: HelpService,
    settings: props.settings
  };
  return <UserVisibilitySectionInternal {...deps} />;
}

/* for registering the component in settings registry */
export const UserVisibilitySpecs = componentRegistryEntry({
  name: 'UserVisibilitySection',
  component: UserVisibilitySection
});

